import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import FormationFormModal, {
  type FormationDepositPayload,
  type FormationCompanyData,
} from '@/components/FormationFormModal';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { lumi } from '@/lib/lumi';
import {
  trackFormationDepositOpen,
  trackFormationDepositPublishedDirect,
  trackFormationDepositDraftSaved,
} from '@/lib/analytics';

// Clé sessionStorage partagée avec DashboardOrganisme.tsx pour finaliser le
// dépôt de formation après création / connexion du compte organisme.
export const PENDING_FORMATION_DRAFT_KEY = 'bnssa-mns:pending-formation-draft';

/**
 * useFormationDepositFlow — Flux mutualisé de dépôt de formation organisme.
 *
 * Même principe que useJobDepositFlow et useAvailabilityDepositFlow :
 * Étape 1 : ouverture directe de la pop-up (FormationFormModal), que le
 *           visiteur soit connecté ou non.
 * Étape 2 : à l'envoi du formulaire :
 *   • organisme/admin connecté → enregistrement des coordonnées + création
 *     immédiate de la formation, puis redirection vers le dashboard organisme ;
 *   • visiteur non connecté → la formation est stockée en brouillon
 *     (sessionStorage) et l'utilisateur est redirigé vers l'inscription
 *     organisme ; le dashboard finalise le dépôt à la première connexion.
 *
 * Garde de rôle : un candidat ou un recruteur connecté ne peut pas déposer
 * une formation (pop-up explicative). Organisme connecté, visiteur non
 * connecté et admin restent autorisés.
 */
export function useFormationDepositFlow() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isCandidate, isRecruiter, isOrganisme, isAdmin } = useAuth();
  const creator = user?.userId || '';
  const email = user?.email || '';
  const { profile: companyProfile, isComplete, upsert: upsertCompany } = useCompanyProfile(
    creator,
    email,
    'organisme'
  );
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canDeposit = isOrganisme || isAdmin;

  const openModal = useCallback(() => {
    // Garde de rôle : candidat ou recruteur connecté → blocage + pop-up.
    if (isAuthenticated && !isAdmin && (isCandidate || isRecruiter)) {
      toast.error(
        isCandidate
          ? "Votre compte est un compte candidat. Seuls les organismes de formation peuvent annoncer une formation."
          : "Votre compte est un compte recruteur. Seuls les organismes de formation peuvent annoncer une formation."
      );
      return;
    }
    trackFormationDepositOpen({ authenticated: isAuthenticated });
    setOpen(true);
  }, [isAuthenticated, isAdmin, isCandidate, isRecruiter]);

  const closeModal = useCallback(() => setOpen(false), []);

  const handleSave = useCallback(
    async (data: FormationDepositPayload) => {
      // Cas 1 : organisme/admin connecté → publication immédiate
      if (isAuthenticated && creator && canDeposit) {
        setSaving(true);
        const now = new Date().toISOString();
        try {
          // 1) Mettre à jour les coordonnées d'organisme si nécessaire
          await upsertCompany({
            raisonSociale: data.company.raisonSociale,
            siret: data.company.siret || '',
            adresse: data.company.adresse || '',
            telephone: data.company.telephone,
            responsableNom: data.company.responsableNom,
            responsableFonction: data.company.responsableFonction || '',
            logoUrl: companyProfile?.logoUrl || '',
          });

          // 2) Créer la formation (en attente de validation admin)
          const sessionIso =
            data.formation.sessionDate.length === 10
              ? `${data.formation.sessionDate}T00:00:00.000Z`
              : new Date(data.formation.sessionDate).toISOString();
          await lumi.entities.organisme_formations.create({
            title: data.formation.title,
            category: data.formation.category,
            organisme: data.company.raisonSociale,
            location: data.formation.location,
            region: data.formation.region,
            duration: data.formation.duration || '',
            sessionDate: sessionIso,
            description: data.formation.description || '',
            programme: data.formation.programme || '',
            prerequisites: data.formation.prerequisites || '',
            certification: data.formation.certification || '',
            contact: data.formation.contact || '',
            creator,
            creatorEmail: data.company.email || email,
            validationStatus: 'pending',
            createdAt: now,
            updatedAt: now,
          });

          trackFormationDepositPublishedDirect({
            category: data.formation.category,
            region: data.formation.region,
          });
          setOpen(false);
          toast.success('Formation soumise. Elle sera publiée après validation par l\'administrateur.');
          navigate('/dashboard-organisme');
        } catch (err) {
          console.error('[useFormationDepositFlow] save failed', err);
          toast.error("Impossible d'enregistrer la formation. Réessayez.");
        } finally {
          setSaving(false);
        }
        return;
      }

      // Cas 2 : non connecté → brouillon + inscription organisme
      try {
        sessionStorage.setItem(PENDING_FORMATION_DRAFT_KEY, JSON.stringify(data));
      } catch {
        // silent
      }
      trackFormationDepositDraftSaved({
        category: data.formation.category,
        region: data.formation.region,
      });
      setOpen(false);
      toast('Créez votre compte organisme pour finaliser votre annonce de formation.', { icon: '📝' });
      navigate('/inscription?role=organisme&next=/dashboard-organisme');
    },
    [isAuthenticated, creator, email, canDeposit, companyProfile, upsertCompany, navigate]
  );

  // Pré-remplissage des coordonnées si l'organisme connecté en possède déjà
  const initialCompany: Partial<FormationCompanyData> | undefined =
    canDeposit && companyProfile
      ? {
          raisonSociale: companyProfile.raisonSociale,
          siret: companyProfile.siret,
          adresse: companyProfile.adresse,
          telephone: companyProfile.telephone,
          email: companyProfile.creatorEmail || email,
          responsableNom: companyProfile.responsableNom,
          responsableFonction: companyProfile.responsableFonction,
        }
      : undefined;

  const submitLabel = isAuthenticated && canDeposit ? 'Soumettre pour validation' : 'Continuer';

  // Repli : si un brouillon de pré-inscription existe (pop-up déjà soumise puis
  // retour sans création de compte), on le réinjecte pour ne jamais afficher un
  // formulaire vide.
  let fallbackDraft: FormationDepositPayload | null = null;
  if (open) {
    try {
      const raw = sessionStorage.getItem(PENDING_FORMATION_DRAFT_KEY);
      if (raw) fallbackDraft = JSON.parse(raw) as FormationDepositPayload;
    } catch {
      fallbackDraft = null;
    }
  }

  const modal = open ? (
    <FormationFormModal
      email={email || undefined}
      initialCompany={initialCompany}
      hideCompanyBlock={!!(canDeposit && isComplete)}
      fallbackDraft={fallbackDraft}
      onClose={closeModal}
      onSave={handleSave}
      submitLabel={submitLabel}
      saving={saving}
    />
  ) : null;

  return { open, openModal, closeModal, modal };
}
